<template>
  <templates-contentful-content
    v-if="post"
    v-bind="propsData"
    :template="template"
  >
    <template
      v-if="template.includes('Article3Column')"
      #hero
    >
      <MoleculesVerticalFormWidgetHero
        v-if="template === 'Article3Column'"
        :vertical="vertical"
        :is-income-protection="isIncomeProtection"
        :is-ovhc="isOvhc"
      />

      <MoleculesVerticalPostHero
        v-else-if="['Article3Column-D1', 'Article3Column-D2', 'Article3Column-D3'].includes(template)"
        :variant="template"
      />
    </template>

    <template #main-content>
      <RichTextRenderer
        :document="post.mainContent.json"
        :node-renderers="renderNodes()"
      />

      <template v-if="template === 'ArticleWithSidebarV2' || template.includes('Article3Column')">
        <AtomsAuthorBlock
          v-if="template.includes('Article3Column')"
          theme="expanded"
          :name="post?.author?.name"
          :image="post?.author?.authorImage?.url"
          :description="post?.author?.authorProfile"
          :dimensions="{
            width: post?.author?.authorImage?.width ?? 200,
            height: post?.author?.authorImage?.height ?? 200
          }"
          :job-title="post?.author?.authorJobTitle"
          :vertical="post?.author?.vertical"
          :tips="post?.author?.tips"
          :url="`/expert-analysis/author${post?.author?.authorUrl}`"
        />

        <hr
          class="mt-8 lg:mt-[2.9375rem] mb-6 lg:mb-10 border-b border-b-gray-400 border-t-0 border-l-0 border-r-0"
        />

        <MoleculesShareButtons
          title="Share this article"
          :buttons="[
            {
              name: 'Facebook',
              url: `https://www.facebook.com/sharer/sharer.php?u=${articleLink}`,
            },
            {
              name: 'Twitter',
              url: `https://twitter.com/share?text=${post.title}&url=${articleLink}`,
            },
            {
              name: 'Linkedin',
              url: `https://www.linkedin.com/shareArticle?mini=true&url=${articleLink}`,
            },
            {
              name: 'Whatsapp',
              url: `https://api.whatsapp.com/send?text=${articleLink}`,
            },
            {
              name: 'Copy',
              text: 'Copy',
              url: articleLink,
            },
          ]"
          image-lazy-load
        />

        <hr class="mt-6 lg:mt-10 border-b border-b-gray-400 border-t-0 border-l-0 border-r-0" />
      </template>
    </template>

    <template
      v-if="template === 'ArticleLightWithSidebar'"
      #main-content-extra
    >
      <div class="px-4 lg:px-0">
        <div class="max-w-5.5xl mx-auto text-center pt-8 pb-16 border-t border-t-gray-300">
          <p class="text-2xl md:text-3.5xl font-bold mb-2">
            Compare today and discover what you can save!
          </p>

          <p class="text-base md:text-lg mb-6">
            Discover the best options for you
          </p>

          <atoms-cta
            :to="verticalData.formPage"
            :full="false"
            align="center"
            theme="primary"
          >
            Get started
          </atoms-cta>
        </div>
      </div>
    </template>

    <template
      v-if="template.includes('Article3Column') && devices.breakpoint('lg')"
      #sidebar-left
    >
      <atoms-headline
        tag="p"
        class="font-bold text-lg !py-0 mb-4"
      >
        In this guide
      </atoms-headline>

      <ul class="flex flex-col gap-4">
        <li
          v-for="(heading, headingKey) in jumpLinks"
          :key="headingKey"
          class="hover:underline hover:font-bold cursor-pointer text-sm"
          @click="handleScrollToJumplink(heading)"
        >
          {{ heading }}
        </li>
      </ul>
    </template>

    <template
      v-if="
        ['ArticleWithSidebarV2', 'ArticleLightWithSidebar'].includes(template) ||
          (template.includes('Article3Column') && devices.breakpoint('lg'))
      "
      #sidebar-right
    >
      <OrganismsContentWidgetsSidebarV2
        v-if="template === 'ArticleWithSidebarV2'"
        :is-income-protection="isIncomeProtection"
        :vertical="vertical"
      />

      <div
        v-if="template.includes('Article3Column') && devices.breakpoint('lg')"
        class="flex flex-col"
      >
        <atoms-headline
          tag="p"
          class="font-bold text-lg !py-0 mb-4"
        >
          Related Articles
        </atoms-headline>

        <atoms-accordion
          v-for="(item, itemKey) in sidebarRelatedArticles"
          :key="itemKey"
          icon-type="arrow"
          size="medium"
          class="!text-base !border-gray-400"
        >
          <template #title>
            {{ item.title }}
          </template>

          <template #content>
            <div class="flex flex-col gap-4 text-sm">
              <nuxt-link
                v-for="(link, linkKey) in item.links"
                :key="linkKey"
                :to="link.url"
                target="_blank"
                class="hover:font-bold hover:underline"
                no-rel
              >
                {{ link.text }}
              </nuxt-link>
            </div>
          </template>
        </atoms-accordion>
      </div>

      <OrganismsContentWidgetsSidebarV1
        v-if="['ArticleLightWithSidebar'].includes(template)"
        :vertical="vertical"
      />
    </template>

    <template
      v-if="template.includes('Article3Column') && devices.breakpoint('lg')"
      #sidebar-right-extra
    >
      <MoleculesTrustpilotCompactReview class="mt-10 border-1 border-gray-400" />
    </template>

    <template
      v-if="
        ['ArticleWithSidebarV2', 'ArticleLightWithSidebar'].includes(template) ||
          (template === 'Article3Column' && devices.breakpoint('lg') && !userLoggedIn)
      "
      #sidebar-right-join-club-plus
    >
      <MoleculesJoinClubPlus class="border-1 border-gray-400" />
    </template>

    <template
      v-if="template === 'ArticleWithSidebarV2' || template.includes('Article3Column')"
      #related-articles
    >
      <MoleculesPostTileGroup
        v-if="!_isEmpty(relatedNewsItems)"
        id="related-news"
        title="Related articles"
        title-size="large"
        title-class="mb-6 text-xl lg:text-2xl"
        tile-class="gap-y-4 lg:gap-x-[4.125rem]"
        :items="relatedNewsItems?.items"
        :items-per-row="template === 'Article3Column' ? 3 : 4"
        image-lazy-load
      />

      <atoms-button
        size="large"
        theme="secondary"
        class="ml-auto mt-6 lg:mt-[4.25rem] w-full lg:w-auto justify-center leading-6 lg:bottom-4 relative"
        :has-icon="true"
        :full="false"
        default="BUTTON"
        @click="() => $router.push(verticalData.guidesUrl)"
      >
        View more
      </atoms-button>
    </template>

    <template
      v-if="
        template === 'ArticleLightWithSidebar' ||
          !template.includes('Article3Column') ||
          (
            !_isEmpty(DISCLAIMER?.[vertical]) &&
            (
              (template === 'ArticleWithSidebarV2' && vertical === 'Energy') ||
              ['ArticleLightWithSidebar'].includes(template)
            )
          )
      "
      #footer
    >
      <div
        v-if="template === 'ArticleLightWithSidebar'"
        :class="layoutMaxWidth"
      >
        <div
          v-if="guidesConfigData.articleEnablePartnerSlider"
          class="px-4 lg:px-0"
        >
          <MoleculesCarouselPartner
            class="max-w-wrapper-md mx-auto text-center pt-8 pb-16"
            :vertical="vertical"
          />
        </div>

        <div
          v-if="guidesConfigData.articleEnableCardCta"
          class="px-4 lg:px-0"
        >
          <div class="max-w-5.5xl mx-auto text-center pt-8 pb-16 border-t border-t-gray-300">
            <p class="text-3.5xl mb-4">
              Get Life Insurance quotes some of Australia’s Top Providers
            </p>

            <p class="text-lg mb-6">
              Compare some of Australia’s top life insurers with one simple form.
            </p>

            <atoms-cta
              :to="verticalData.formPage"
              :full="false"
              align="center"
              theme="primaryWhite"
            >
              Get started
            </atoms-cta>
          </div>
        </div>
      </div>

      <AtomsAuthorBlock
        v-if="!template.includes('Article3Column')"
        theme="compact"
        :name="post?.author?.name"
        :image="post?.author?.authorImage?.url"
        :description="post?.author?.authorProfile"
        :dimensions="{
          width: post?.author?.authorImage?.width ?? 200,
          height: post?.author?.authorImage?.height ?? 200
        }"
        :job-title="post?.author?.authorJobTitle"
      />

      <div
        v-if="
          !_isEmpty(DISCLAIMER?.[vertical]) &&
            (
              (template === 'ArticleWithSidebarV2' && vertical === 'Energy') ||
              ['ArticleLightWithSidebar'].includes(template)
            )
        "
        class="!py-16 !bg-neutral-50"
      >
        <molecules-home-footer-info
          theme="white-boxed"
          class="!py-0 !bg-transparent"
          :class="layoutMaxWidth"
        >
          <template #title>
            Things you should know
          </template>
          <template #description>
            <!-- eslint-disable vue/no-v-html -->
            <div v-html="DISCLAIMER[vertical]"></div>
          </template>
        </molecules-home-footer-info>
      </div>
    </template>
  </templates-contentful-content>
</template>

<script setup>
import _isEmpty from 'underscore/cjs/isEmpty.js'
import _find from 'underscore/cjs/find.js'

import RichTextRenderer from 'contentful-rich-text-vue-renderer'
import ArticlesService from '~/server/contentful/articles.service'

// variables
import { GUIDES_CONFIG } from '~/helpers/constants'
import { PAGE_TYPES, TEMPLATES } from '~/constants/pages/contentful.js'
import {
  DISCLAIMER,
  RELATED_ARTICLES
} from '~/constants/pages/contentful'
import { POST_TYPES } from '~/constants/verticals.constants'

import {
  RENDER_NODES,
  GET_FEATURE_IMAGE
} from '~/helpers/contentful'

defineOptions({
  name: 'TemplatesContentfulGuides'
})

const props = defineProps({
  isPreview: {
    type: Boolean,
    default: false
  }
})

const {
  internalDomainForInlinesHyperlink
} = useEnv()

const {
  fetchRelatedArticles
} = new ArticlesService()

const $route = useRoute()
const $router = useRouter()
const layoutsStore = useLayoutsStore()
const { devices } = deviceChecker()
const userStore = useUserStore()
const userLoggedIn = computed(() => userStore.isUserLoggedIn)

if (!$route.params.slug) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
    fatal: true
  })
}

const tempVertical = (() => { 
  const temp = $route.params?.vertical || ''

  if (!temp) {
    const path = $route.path
    const pathArr = path.split('/')
      .filter(item => item)
      
    return pathArr?.[0]
  }

  return temp
})()

const slug = `/${tempVertical}/${$route.params.slug}`
const vertical = tempVertical && strToCapitalize(tempVertical.replace(/-/g, ' '))

const articleLink = import.meta.client ? window.location.href : ''

const post = await useFetch('/api/contentful/contents/get-article', {
  method: 'post',
  body: {
    vertical,
    slug,
    preview: props.isPreview
  }
}).then(response => { 
  if (!_isEmpty(response?.error?.value?.data)) { 
    throw(response?.error?.value?.data)
  }

  if (!response?.data.value?.exist) {
    throw createError({
      statusCode: 404,
      statusMessage: 'POST DOES NOT EXIST',
      fatal: true
    })
  }

  return response?.data.value || null
}).catch(err => {
  throw createError(err)
})

if (post?.redirectUrl?.slug) {
  await navigateTo(`${post?.redirectUrl?.slug}/`, {
    redirectCode: 301
  })
}

const template = TEMPLATES.includes(post?.template)
  ? post?.template
  : 'ArticleWithSidebarV2'

const jumpLinks = computed(() => {
  return post?.mainContent.json.content
    .filter(item => item.nodeType === 'heading-2')
    .map(item => item?.content[0])
    .map(item => item.value.replace(/[/\n]/g, ''))
})

onMounted(async () => {
  await nextTick()

  // adds id to jumplinks
  for (let i = 0; i < jumpLinks.value.length; i++) {
    const text = jumpLinks.value[i]
    const els = document.querySelectorAll('#article-main-content h2')

    const curr = _find(els, (value, key) => (!isNaN(+key) ? removeHtmlString(value.innerText) === text : null))

    if (curr) {
      curr.setAttribute('id', strToKebabCase(text))
    }
  }
})

onBeforeMount(() => {
  layoutsStore.navigation.ctaOnly = template === 'ArticleLightWithSidebar'
})

onUnmounted(() => {
  layoutsStore.navigation.ctaOnly = false
})

const relatedArticlesLimit = computed(() => {
  if (['Article3Column', 'Article3Column-D1', 'Article3Column-D2', 'Article3Column-D3'].includes(template)) {
    if (devices.breakpoint('md', 'max')) {
      return 4
    }

    return 3
  }

  return 4
})

const relatedArticles = await (async () => {
  if (
    !post?.exist ||
    !['ArticleWithSidebarV2', 'Article3Column'].includes(template)
  ) {
    return []
  }
  
  const limit = relatedArticlesLimit.value
  const { data } = await useAsyncData(() => fetchRelatedArticles(
    vertical,
    slug,
    false,
    limit
  ))
    .then(response => response)
    .catch(err => {
      // eslint-disable-next-line no-console
      console.error(err)
    })
  
  return data.value?.total > 0
    ? data.value.items  
    : []
})()

const relatedNewsItems = computed(() => {
  const _items = JSON.parse(JSON.stringify(relatedArticles))

  if (_isEmpty(_items)) {
    return {}
  }

  if (_items.length !== relatedArticlesLimit.value) {
    _items.splice(-1)

    return {
      featured: {
        exist: false
      },
      total: _items.length,
      items: _items
    }
  }

  return {
    featured: {
      exist: false
    },
    total: _items.length,
    items: _items
  }
})

const sidebarRelatedArticles = RELATED_ARTICLES?.[vertical] || null

const verticalData = VERTICALS[vertical || VERTICAL.DEFAULT]
const guidesConfigData = GUIDES_CONFIG.config[vertical || VERTICAL.DEFAULT]
const isIncomeProtection = !!post?.isIncomeProtection
const isOvhc = !!post?.isOvhc

const layoutMaxWidth = (() => { 
  return getKey(template, {
    default: 'lg:max-w-none',
    ArticleLightWithSidebar: 'lg:max-w-none',
    ArticleWithSidebarV2: 'lg:max-w-wrapper-sm',
    Article3Column: 'lg:max-w-wrapper-xs2'
  }) + ' w-full md:max-w-[42.0625rem] mx-auto'
})()

const propsData = computed(() => {
  const author = (() => {
    const temp = post?.author

    return {
      name: temp?.name ?? 'Compare Club',
      url: `/expert-analysis/author${temp?.authorUrl}`,
      image: {
        url: temp?.authorImage?.url ?? FEATURED_IMAGE_DEFAULT_IMAGE
      }
    }
  })()

  const postType = (() => {
    const type = POST_TYPES.find(item => item.type === post.postType)

    return {
      name: type?.name,
      url: type?.type !== 'Articles'
        ? `${verticalData.productPageUrl}${type?.urlSlug}/`
        : ''
    }
  })()

  const breadcrumbs = (() => {
    return [
      {
        text: 'Home',
        name: 'Home',
        url: '/'
      },
      {
        text: verticalData.guides_index_title,
        name: verticalData.guides_index_title,
        url: verticalData.productPageUrl
      },
      {
        text: postType.name,
        name: postType.name,
        url: postType.url
      },
      {
        text: (() => {
          if (devices.isMobile.value) {
            return post.title.slice(0, 32) + (post.title.length > 32 ? '...' : '')
          }

          return post.title
        })(),
        url: '/',
        name: 'Title',
        active: true
      }
    ]
  })()

  const propsObj = {
    title: post.title,
    subTitle: post.subTitle,
    breadcrumbs,
    lastUpdate: post?.date ?? post.sys.publishedAt,
    featuredImage: GET_FEATURE_IMAGE(post.featureImage, props.template, devices.currBreakpoint.value),
    author,
    vertical,
    slug: post.slug,
    hasShareButtons: false,
    postType: post.postType,
    tags: (() => {
      const temp = !_isEmpty(post?.contentfulMetadata?.tags)
        ? post?.contentfulMetadata.tags
          .filter(tag => tag.name === 'Fact Checked')
          .map(tag => {
            return {
              name: tag.name
            }
          })
        : []

      return [
        ...temp,
        {
          name: verticalData.guides_index_title
        }
      ]
    })(),
    ctaText: guidesConfigData?.acticleCtaText
      ? guidesConfigData.acticleCtaText
      : 'Get free quotes',
    layoutMaxWidth
  }

  return propsObj
})

const entriesMap = (() => {
  const map = new Map()

  if (_isEmpty(post)) {
    return map
  }

  const links = post.mainContent?.links

  for (const entry of links.entries.block) {
    map.set(entry.sys.id, entry)
  }

  return map
})()

const assetsMap = (() => {
  const map = new Map()

  if (_isEmpty(post)) {
    return map
  }

  const links = post.mainContent?.links

  for (const assets of links.assets.block) {
    map.set(assets.sys.id, assets)
  }

  return map
})()

const renderNodes = () => RENDER_NODES({
  entriesMap,
  assetsMap,
  vertical,
  template,
  internalDomainForInlinesHyperlink,
  author: post?.author,
  inlineComponents: {
    CTA: resolveComponent('AtomsCta'),
    CALCULATOR: resolveComponent('MoleculesCalculatorWithDisclaimer'),
    KeyPoints: resolveComponent('MoleculesKeyPoints'),
    InlineAge: resolveComponent('MoleculesInlineAge'),
    InlineCardCta: resolveComponent('MoleculesInlineCardCta'),
    InlineLifeStage: resolveComponent('MoleculesInlineLifeStage'),
    InlinePartner: resolveComponent('MoleculesInlinePartner'),
    VerticalFormWidget: resolveComponent('MoleculesVerticalFormWidgetSidebar'),

    MoleculesInlineLifeStage: resolveComponent('MoleculesInlineLifeStage'),
    Lifestage: resolveComponent('OrganismsInlineLifeStageAbTest'),
    State: resolveComponent('MoleculesInlineCardCta'),
    InlineAgeAbTest: resolveComponent('OrganismsInlineAgeAbTest'),
    AgeRange: resolveComponent('MoleculesInlineCardCta'),
    Partner: resolveComponent('MoleculesInlinePartner'),

    AUTHORBLOCK: resolveComponent('AtomsAuthorBlock'),
    FaqItem: resolveComponent('AtomsAccordion'),

    TextBox: resolveComponent('MoleculesHomeFooterInfo'),

    VideoAsset: resolveComponent('AtomsVideoFrame')
  }
})

const noCanonicalLink = !!post?.noCanonicalLink
const noIndex = props.isPreview
  ? false
  : !!post?.noIndex
const canonicalLink = post?.canonicalLink || `${CC_DOMAIN}${post?.slug}/`

const activeHeading = ref()

function handleScrollToJumplink (heading) {
  const id = strToKebabCase(heading)
  activeHeading.value = id

  const el = document.getElementById(id)

  if (el) {
    scrollToId(el)
  }
}

generateMeta({
  title: post?.title || 'Compare Club',
  ogTitle: post?.seoTitle ?? post?.title ?? 'Compare Club',

  description: post?.metaDescription ?? '',
  ogDescription: post?.metaDescription ?? '',

  canonical: noCanonicalLink ? '' : canonicalLink,
  ogUrl: canonicalLink,
  ogImage: propsData.value.featuredImage?.url || FEATURED_IMAGE_DEFAULT_IMAGE,
  ogType: PAGE_TYPES?.[post.postType] || '',
  meta: [
    {
      itemprop: 'mainEntityOfPage',
      content: canonicalLink
    },
    {
      itemprop: 'dateModified',
      content: post.sys.publishedAt
    }
  ],
  noIndex,
  noFollow: noIndex
})

useJsonld(() => {
  const faqList = !_isEmpty(post?.faqSchema) && !post.hideFaqSchema
    ? post?.faqSchema
    : undefined

  const faqSchema = (faqList && {
    '@type': 'FAQPage',
    mainEntity: faqList.map(item => ({
      '@type': 'Question',
      name: item?.key ?? '',
      acceptedAnswer: {
        '@type': 'Answer',
        text: item?.value ?? ''
      }
    }))
  }) || false

  return {
    '@context': 'http://schema.org',
    '@graph': [
      faqSchema,
      {
        '@type': 'Article',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': $route.fullPath
        },
        headline: post?.title,
        description: post?.subTitle,
        image: post?.featureImage?.url,
        author: {
          '@type': 'Person',
          name: post?.author?.name,
          url: `https://compareclub.com.au/expert-analysis/author${post?.author?.authorUrl}`
        },
        publisher: {
          '@type': 'Organization',
          name: 'Compare Club',
          logo: {
            '@type': 'ImageObject',
            url: 'https://compareclub.com.au/_ipx/s_190x30/images/cc-logo-new.svg'
          }
        },
        datePublished: post?.sys.firstPublishedAt,
        dateModified: typeof post?.date === 'undefined'
          ? post?.sys.publishedAt
          : post?.date
      }
    ].filter(item => item)
  }
})
</script>
